import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

import Home from "./components/Home";
import AuthCallback from "./components/AuthCallback";
import LoginButton from "./components/LoginButton";
import ErrorBoundary from "./components/ErrorBoundary";
import AdminDashboard from "./components/AdminDashboard";
import OpenInSafari from "./components/OpenInSafari";

const getBackendUrl = () => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("test.magicmixapp.ch")) {
      return "https://test.magicmixapp.ch";
    } else if (hostname.includes("magicmixapp.ch")) {
      return "https://magicmixapp.ch";
    }
  }
  return process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || "https://test.magicmixapp.ch";
};

const API = `${getBackendUrl()}/api`;

// Global error handler to prevent crashes from unhandled errors
if (typeof window !== 'undefined') {
  window.onerror = function(message, source, lineno, colno, error) {
    console.error('Global error caught:', { message, source, lineno, colno, error });
    // Don't show error page, just log it
    return true;
  };
  
  window.onunhandledrejection = function(event) {
    console.error('Unhandled promise rejection:', event.reason);
    // Prevent the default browser behavior (error page)
    event.preventDefault();
    return true;
  };
}

const AppRouter = () => {
  const location = useLocation();
  // Il nuovo buttafuori: inizia come 'null' e blocca tutto finché non è sicuro
  const [user, setUser] = useState(null);
  const [isChecking, setIsChecking] = useState(true); 

  const isAuthCallback = location.hash?.includes('session_id=');

  useEffect(() => {
    if (isAuthCallback) {
      setIsChecking(false);
      return;
    }
    
    const checkSession = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (err) {
        setUser(null);
      } finally {
        // Che sia loggato o no, il controllo è finito. Apriamo la porta giusta.
        setIsChecking(false); 
      }
    };
    checkSession();
  }, [isAuthCallback]);

  const handleLogout = async () => {
    try {
      // Prova ad avvisare il server
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      // A prescindere dalla risposta del server, pialla tutto e sbattilo fuori
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload(); // Forza il muro di login
    }
  };

  if (isAuthCallback) {
    return <AuthCallback />;
  }

  // ⏳ STATO 1: SALA D'ATTESA (Splash Screen - Il server verifica chi sei)
  if (isChecking) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100vw', backgroundColor: '#0a0a0a', position: 'fixed', top: 0, left: 0, zIndex: 9999 }}>
        <h1 style={{ color: '#ffffff', fontSize: '28px', letterSpacing: '4px', margin: 0, opacity: 0.5 }}>MAGIC MIX</h1>
        <p style={{ color: '#00f0ff', fontSize: '12px', marginTop: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>Verifica sicurezza in corso...</p>
      </div>
    );
  }

  // 🛡️ STATO 2: IL MURO DI LOGIN (Colori allineati al nuovo logo ufficiale)
  // Bypass per completare il doppio salto del Magic Link prima del muro di login
  if (location.pathname === "/open-in-safari") {
    return <OpenInSafari />;
  }

  

  // 🟢 STATO 3: I CANCELLI SONO APERTI (Se il server dice che SEI loggato)
  return (
    <>
      <div className="mobile-nav" data-testid="mobile-nav" style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 999999, pointerEvents: 'auto' }}>
        <LoginButton user={user} onLogout={handleLogout} mode="button" />
      </div>
      <Routes>
        <Route path="/" element={
          <ErrorBoundary onRetry={() => window.location.reload()}>
            <Home user={user} />
          </ErrorBoundary>
        } />
        <Route path="/admin" element={
          <ErrorBoundary onRetry={() => window.location.reload()}>
            <AdminDashboard user={user} />
          </ErrorBoundary>
        } />
      </Routes>
    </>
  );
};

// 👇 La funzione principale che era saltata
function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary onRetry={() => window.location.reload()}>
        <AppRouter />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;